import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from '../internal/cx';
import { Portal } from '../internal/Portal';
import { useAnchoredPosition } from '../internal/useAnchoredPosition';
import styles from './Tooltip.module.css';

interface DescribableProps {
  'aria-describedby'?: string | undefined;
}

type DescribableElement = ReactElement<DescribableProps>;

export interface TooltipProps {
  /** Supplementary text. Tooltips must never be the only way to convey essential information. */
  content: ReactNode;
  /**
   * A single focusable element (button, link, input...). When `describeChild` is true it must be
   * an element so aria-describedby can be attached.
   */
  children: DescribableElement | ReactNode;
  placement?: 'top' | 'bottom' | 'right' | 'left';
  /** Hover delay before showing, in ms. Keyboard focus shows immediately. */
  delay?: number;
  /**
   * Link the tooltip to the child via aria-describedby (default). Set false when the tooltip
   * repeats the child's accessible name (e.g. icon-button labels) to avoid double announcement.
   */
  describeChild?: boolean;
  disabled?: boolean;
  className?: string;
}

const HIDE_DELAY = 120;

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 400,
  describeChild = true,
  disabled = false,
  className,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimer = useRef<number | undefined>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);

  const visible = open && !disabled && content != null && content !== '';

  useAnchoredPosition({
    open: visible,
    anchorRef,
    floatingRef: tooltipRef,
    placement,
    align: 'center',
    offset: 8,
  });

  useEffect(() => {
    if (!visible) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  useEffect(
    () => () => {
      window.clearTimeout(showTimer.current);
      window.clearTimeout(hideTimer.current);
    },
    [],
  );

  function show(immediate: boolean) {
    window.clearTimeout(hideTimer.current);
    window.clearTimeout(showTimer.current);
    if (immediate || delay <= 0) setOpen(true);
    else showTimer.current = window.setTimeout(() => setOpen(true), delay);
  }

  function hide() {
    window.clearTimeout(showTimer.current);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setOpen(false), HIDE_DELAY);
  }

  const child =
    describeChild && !disabled && isValidElement<DescribableProps>(children)
      ? cloneElement(children, {
          'aria-describedby': cx(children.props['aria-describedby'], id) || undefined,
        })
      : children;

  return (
    <>
      <span
        ref={anchorRef}
        className={styles.anchor}
        onMouseEnter={() => show(false)}
        onMouseLeave={hide}
        onFocus={() => show(true)}
        onBlur={hide}
      >
        {child}
      </span>
      {!disabled && (
        <Portal>
          <div
            ref={tooltipRef}
            id={id}
            role="tooltip"
            hidden={!visible}
            className={cx(styles.tooltip, className)}
            onMouseEnter={() => window.clearTimeout(hideTimer.current)}
            onMouseLeave={hide}
          >
            {content}
          </div>
        </Portal>
      )}
    </>
  );
}
